import React from "react";
import { makeStyles } from "@material-ui/core/styles";

type RadioButtonProps = {
  name: string;
  value: string | number;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  size?: number; // default 18px
  color?: string; // default green
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    gap: 8,
  },

  radioWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  root: (props: {
    size: number;
    color: string;
    disabled: boolean;
    checked: boolean;
  }) => ({
    width: props.size,
    height: props.size,
    borderRadius: "50%",
    border: `1px solid ${props.color}`,
    backgroundColor: props.checked ? props.color : "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    cursor: props.disabled ? "not-allowed" : "pointer",
    flexShrink: 0,
  }),

  innerDot: (props: { size: number }) => ({
    width: props.size * 0.35,
    height: props.size * 0.35,
    borderRadius: "50%",
    backgroundColor: "#fff",
  }),

  hiddenInput: {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
    pointerEvents: "none",
  },

  childrenStyle: {
    display: "flex",
  },
});

const RadioButton: React.FC<RadioButtonProps> = ({
  name,
  value,
  checked,
  onChange,
  size = 18,
  color = "#8CC63F",
  disabled = false,
  className = "",
  children,
}) => {
  const classes = useStyles({ size, color, disabled, checked } as any);

  const handleClick = () => {
    if (!disabled) {
      // manually trigger the input change
      const event = {
        target: { name, value },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(event);
    }
  };

  return (
    <div
      className={`${classes.container} ${className}`}
      onClick={handleClick}
      role="radio"
      aria-checked={checked}
    >
      <div className={classes.radioWrapper}>
        {/* Hidden native radio input (for forms + accessibility) */}
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className={classes.hiddenInput}
        />

        {/* Custom visual radio */}
        <div className={classes.root}>
          {checked && <div className={classes.innerDot} />}
        </div>
      </div>

      {children && <div className={classes.childrenStyle}>{children}</div>}
    </div>
  );
};

export default RadioButton;
